import {
  afterNextRender,
  Component,
  computed,
  DOCUMENT,
  effect,
  inject,
  PLATFORM_ID,
  Signal,
  signal,
  untracked,
  WritableSignal
} from '@angular/core';
import {RouterOutlet} from '@angular/router';
import {NavbarComponent} from '../shared/component/navbar/navbar.component';
import {Toast} from 'primeng/toast';
import {CookieConsentComponent} from '../shared/component/cookie-consent/cookie-consent.component';
import {ConsentLocalStorageService} from '../shared/local-storage/consent/consent-local-storage.service';
import {CookieConsentService} from '../shared/component/cookie-consent/cookie-consent.service';
import {GtmDebugPanelComponent} from '../gtm-debug-panel/gtm-debug-panel.component';
import {updatePrimaryPalette} from '@primeuix/themes';
import {Palette} from '../shared/type/palette.type';
import {Theme} from '../shared/enum/theme.enum';
import {auctionsPalette} from '../shared/palette/auctions.palette';
import {parkingPalette} from '../shared/palette/parking.palette';
import {defaultPalette} from '../shared/palette/default.palette';
import {Button} from 'primeng/button';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import {isPlatformBrowser} from '@angular/common';
import {googleAnalyticsDebugSignal} from '../shared/signal/common/analytics/google-analytics-debug.signal';
import {CookieConsentModel} from '../shared/enum/cookie-consent.enum';
import {ConsentStatus} from '../shared/enum/gtm-cookie-consent.enum';
import {GoogleAnalyticsService} from '../shared/analytics/google/google-analytics.service';
import {PixelService} from '../shared/analytics/meta/pixel.service';
import {routingMetadata} from '../shared/signal/common/routing-matadata.signal';
import {RoutingMetadataResolver} from '../shared/resolver/routing-metadata.resolver';
import {navigatorScrollingSignal} from '../shared/signal/common/navigator-scrolling.signal';
import {PixelEventName, ScrollEvent} from '../shared/analytics/meta/types/pixel.types';
import {MicrosoftClarityService} from '../shared/analytics/microsoft/microsoft-clarity.service';
import {MicrosoftClaritySessionBumper} from '../shared/analytics/microsoft/types/microsoft-clarity-session-bumper.enum';
import {isViewInitializedSignal} from '../shared/signal/common/is-view-initialized.signal';
import {ProgressSpinner} from 'primeng/progressspinner';
import {PhoneBannerComponent} from '../shared/component/phone-banner/phone-banner.component';
import {phoneBannerVisibilitySignal} from '../shared/signal/common/phone-banner-visibility.signal';
import {PrimeTemplate} from 'primeng/api';
import {NgIcon, provideIcons} from '@ng-icons/core';
import {tablerCookie} from '@ng-icons/tabler-icons';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NavbarComponent, Toast, CookieConsentComponent, GtmDebugPanelComponent, Button, ProgressSpinner, PhoneBannerComponent, PrimeTemplate, NgIcon],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  providers: [
    provideIcons({
      tablerCookie
    })
  ]
})
export class App {
  public readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly title: WritableSignal<string> = signal('landings-frontend');
  protected logotypeUrl: Signal<string> = computed(() => routingMetadata()?.logotypeUrl || ' ');
  protected readonly googleAnalyticsDebugEnabled: WritableSignal<boolean> = googleAnalyticsDebugSignal;
  protected isCookieConsentDialogVisible = signal(false);
  protected scrollIcon: string = 'pi pi-check';
  protected arrowUp: boolean = false;
  protected readonly isViewInitializedSignal = isViewInitializedSignal;
  protected isCookieConsentDialogEnabled: boolean = false;

  private readonly CONSENT_TIMEOUT: number = 3000;
  private readonly document = inject(DOCUMENT);
  private gsap: any;
  private ScrollTrigger: any;
  private isNavigatorScrolling: boolean = false;
  private readonly seoScrollEvents: ScrollEvent[] = [
    {percentage: 25, event: PixelEventName.Scroll25},
    {percentage: 50, event: PixelEventName.Scroll50},
    {percentage: 75, event: PixelEventName.Scroll75},
  ];
  private isPhoneBannerShown: boolean = false;

  constructor(
    private readonly consentLocalStorageService: ConsentLocalStorageService,
    private readonly cookieConsentService: CookieConsentService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly pixelService: PixelService,
    private readonly routingMetadataResolver: RoutingMetadataResolver,
    private readonly microsoftClarityService: MicrosoftClarityService) {

    this.routingMetadataResolver.init();

    this.registerMetaEffect();
    this.registerNavigatorEffect()
    this.registerConsentChangesEffect();

    afterNextRender(async () => {
      await this.setupScrollDetection();
      this.registerSeoScrollEventCallbacks();
      this.initAnalytics();
      this.restoreConsent();
      this.handleConsentBanner();
    });
  }

  protected onScrollButtonClick() {
    if (!this.isBrowser) return;
    if (this.arrowUp) {
      this.gsap.to(window, {
        scrollTo: {y: 0},
        duration: 1,
        ease: 'power2.inOut'
      });
    } else {
      this.gsap.to(window, {
        scrollTo: {y: window.innerHeight},
        duration: 1,
        ease: 'power2.inOut'
      });
    }
  }

  private registerMetaEffect() {
    effect(() => {
      const meta = routingMetadata();
      isViewInitializedSignal.set(false);
      this.initTheme(meta?.theme);
      this.initFavicon(meta?.faviconUrl);

      if (this.isBrowser) {
        setTimeout(() => {
          requestAnimationFrame(() => isViewInitializedSignal.set(true));
        }, 0);
      }
    });
  }

  private registerNavigatorEffect() {
    effect(() => {
      this.isNavigatorScrolling = navigatorScrollingSignal();
    });
  }

  private async setupScrollDetection(): Promise<void> {
    const gsapModule = await import('gsap');
    this.gsap = gsapModule.default ?? gsapModule;

    const scrollTriggerModule = await import('gsap/ScrollTrigger');
    this.ScrollTrigger = scrollTriggerModule.ScrollTrigger;

    this.gsap.registerPlugin(this.ScrollTrigger);
    this.gsap.registerPlugin(ScrollToPlugin)

    this.ScrollTrigger.create({
      trigger: this.document.body,
      start: "top top",
      end: "+=100vh",
      onEnter: () => this.switchIcon(true),
      onLeaveBack: () => this.switchIcon(false)
    });
  }

  private switchIcon(up: boolean) {
    if (!this.isBrowser) return;
    this.arrowUp = up;

    const btnIcon = this.document.querySelector('.scroll-nav-btn .p-button-icon') as HTMLElement;
    if (!btnIcon) return;

    this.gsap.to(btnIcon, {
      rotate: up ? -90 : 0,
      duration: 0.35,
      ease: 'power2.out',
      onComplete: () => {
        this.scrollIcon = up ? 'pi pi-arrow-up' : 'pi pi-arrow-right';
      }
    });
  }

  private initAnalytics(): void {
    if (!this.isBrowser) return;
    this.googleAnalyticsService.initGtm();
  }

  private registerConsentChangesEffect() {
    effect(() => {
      const consent = this.cookieConsentService.cookieConsentSignal();

      if (consent !== null) {
        untracked(() => this.manageConsent(consent));
      }
    });
  }

  private manageConsent(consent: CookieConsentModel | null): void {
    if (!consent) return;

    switch (consent) {
      case CookieConsentModel.All:
        this.googleAnalyticsService.updateConsent(ConsentStatus.Granted);
        this.pixelService.init();
        this.microsoftClarityService.init();
        break;
      case CookieConsentModel.Required:
        this.googleAnalyticsService.updateConsent(ConsentStatus.Denied);
        this.pixelService.destroy();
        break;
      default:
        break;
    }
  }

  private initTheme(theme?: Theme): void {
    theme ??= Theme.BASE;

    let palette: Palette;

    switch (theme) {
      case Theme.AUCTIONS:
        palette = auctionsPalette;
        break;
      case Theme.PARKING:
        palette = parkingPalette;
        break;
      default:
        palette = defaultPalette;
        break;
    }

    if (this.isBrowser) {
      Object.values(Theme).forEach(t => this.document.body.classList.remove(t));
      this.document.body.classList.add(theme);
    }

    updatePrimaryPalette(palette);
  }

  private initFavicon(faviconUrl: string): void {

    const links = this.document.querySelectorAll("link[rel*='icon']");
    links.forEach(link => link.parentNode?.removeChild(link));

    const newLink = this.document.createElement('link');
    newLink.rel = 'icon';
    newLink.type = 'image/x-icon';
    newLink.href = faviconUrl;

    this.document.head.appendChild(newLink);
  }

  private registerSeoScrollEventCallbacks() {
    const fired = new Set<number>();

    window.addEventListener('scroll', () => {
      const percentage = Math.round(
        ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
      );

      this.seoScrollEvents.forEach(scrollEvent => {
        if (!fired.has(scrollEvent.percentage) && percentage >= scrollEvent.percentage) {
          fired.add(scrollEvent.percentage);
          if (!this.isNavigatorScrolling) this.handleScrollEvent(scrollEvent);
        }
      });
    }, {passive: true});
  }

  private handleScrollEvent(scrollEvent: ScrollEvent) {
    this.pixelService.trackEvent(
      scrollEvent.event, {
        percentage: scrollEvent.percentage
      });

    this.microsoftClarityService.trackEvent(`scroll_${scrollEvent.percentage}`);

    if (!this.isPhoneBannerShown && scrollEvent.percentage === 50) {
      this.firePhoneBannerEvent();
      this.isPhoneBannerShown = true;
    }

    if (scrollEvent.percentage === 75) {
      this.microsoftClarityService.upgradeSession(MicrosoftClaritySessionBumper.MostPageScrolled);
    }
  }

  private handleConsentBanner(): void {
    const consent = this.consentLocalStorageService.get();

    if (consent === null) {
      setTimeout(() => {
        this.isCookieConsentDialogVisible.set(true);
        this.isCookieConsentDialogEnabled = true;
      }, this.CONSENT_TIMEOUT);
    } else {
      this.isCookieConsentDialogEnabled = true;
    }
  }

  private restoreConsent(): void {
    const consent = this.consentLocalStorageService.get();

    if (consent !== null) {
      this.cookieConsentService.setConsent(consent);
    }
  }

  private firePhoneBannerEvent() {
    phoneBannerVisibilitySignal.set(true);
  }

  protected onCookieConsentHandle(): void {
    this.isCookieConsentDialogVisible.set(true);
  }
}
